/**
 * <mellow-markdown-editor>: a textarea for Markdown with a formatting toolbar, list
 * continuation on Enter and a "Preview" tab rendered by the server (exactly what
 * <mellow:markdown> shows). The textarea is the value, so wire:model and forms work natively.
 */

// Wrap the selection: [before, after, placeholder]
const WRAP = {
    bold: ['**', '**'],
    italic: ['_', '_'],
    strike: ['~~', '~~'],
    code: ['`', '`'],
};

// Toggle a prefix on every selected line.
const PREFIX = {
    heading: '## ',
    bullet: '- ',
    ordered: '1. ',
    tasks: '- [ ] ',
    quote: '> ',
};

const LIST_ITEM = /^(\s*)([-*+] \[[ xX]\] |[-*+] |(\d+)([.)]) |> )/;

export class MellowMarkdownEditor extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.toolbar?.addEventListener('click', (event) => {
            const button = event.target.closest('[data-command]');
            if (button) this.run(button.dataset.command);
        });
        this.toolbar?.addEventListener('keydown', (event) => this.onToolbarKeydown(event));
        // Keep the selection in the textarea while clicking a tool.
        this.toolbar?.addEventListener('pointerdown', (event) => {
            if (event.target.closest('[data-command]')) event.preventDefault();
        });

        this.tablist?.addEventListener('click', (event) => {
            const tab = event.target.closest('[role="tab"]');
            if (tab) this.select(tab.dataset.tab);
        });
        this.tablist?.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const next = this.mode === 'write' ? 'preview' : 'write';
            const target = event.key === 'Home' ? 'write' : event.key === 'End' ? 'preview' : next;
            this.select(target);
            this.tab(target)?.focus();
        });

        this.input?.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    get input() {
        return this.querySelector('textarea');
    }

    get toolbar() {
        return this.querySelector('[role="toolbar"]');
    }

    get tablist() {
        return this.querySelector('[role="tablist"]');
    }

    get preview() {
        return this.querySelector('.mellow-markdown-preview');
    }

    get mode() {
        return this.tab('preview')?.getAttribute('aria-selected') === 'true' ? 'preview' : 'write';
    }

    tab(name) {
        return this.querySelector(`[role="tab"][data-tab="${name}"]`);
    }

    get buttons() {
        return [...(this.toolbar?.querySelectorAll('[data-command]') ?? [])];
    }

    /** Livewire re-syncs: nothing is derived from the value, so there is nothing to redo. */
    sync() {}

    select(mode) {
        const preview = mode === 'preview';
        for (const name of ['write', 'preview']) {
            const tab = this.tab(name);
            tab?.setAttribute('aria-selected', String(name === mode));
            tab?.setAttribute('tabindex', name === mode ? '0' : '-1');
        }
        this.querySelector('.mellow-markdown-write').hidden = preview;
        this.preview.hidden = !preview;
        this.buttons.forEach((button) => (button.disabled = preview));
        if (preview) this.render();
    }

    async render() {
        const markdown = this.input.value;
        const preview = this.preview;

        if (!markdown.trim()) {
            preview.replaceChildren(Object.assign(document.createElement('p'), { className: 'mellow-markdown-empty', textContent: this.dataset.empty }));
            return;
        }

        preview.setAttribute('aria-busy', 'true');
        try {
            const response = await fetch(this.dataset.previewUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ markdown, locale: this.dataset.locale, breaks: this.dataset.breaks !== 'false' }),
            });
            if (!response.ok) throw new Error(response.statusText);
            const { html } = await response.json();
            // Sanitized by the server (the same allowlist as <mellow:markdown>).
            if (this.input.value === markdown) preview.innerHTML = html;
        } catch {
            preview.replaceChildren(Object.assign(document.createElement('p'), { className: 'mellow-markdown-empty', textContent: this.dataset.failed }));
        } finally {
            preview.setAttribute('aria-busy', 'false');
        }
    }

    run(command) {
        const input = this.input;
        input.focus();

        if (WRAP[command]) return this.wrap(...WRAP[command]);
        if (PREFIX[command]) return this.prefixLines(PREFIX[command]);

        const { selectionStart: start, selectionEnd: end, value } = input;
        const selected = value.slice(start, end);

        if (command === 'link') {
            const text = selected || this.dataset.linkText || 'link';
            this.replace(start, end, `[${text}](https://)`);
            // Select the URL part, ready to paste.
            const url = start + text.length + 3;
            input.setSelectionRange(url, url + 8);
        } else if (command === 'codeblock') {
            const before = this.blankLineBefore(value, start);
            this.replace(start, end, `${before}\`\`\`\n${selected}\n\`\`\`\n`);
            const cursor = start + before.length + 4;
            input.setSelectionRange(cursor, cursor + selected.length);
        } else if (command === 'table') {
            const before = this.blankLineBefore(value, start);
            const column = this.dataset.column || 'Column';
            this.replace(start, end, `${before}| ${column} | ${column} |\n| --- | --- |\n|  |  |\n`);
        }
    }

    /** Blocks need an empty line before them – otherwise they continue the list or paragraph above. */
    blankLineBefore(value, position) {
        const text = value.slice(0, position);
        if (text === '' || text.endsWith('\n\n')) return '';
        return text.endsWith('\n') ? '\n' : '\n\n';
    }

    wrap(before, after) {
        const input = this.input;
        const { selectionStart: start, selectionEnd: end, value } = input;
        const selected = value.slice(start, end);

        // Already wrapped: unwrap.
        if (value.slice(start - before.length, start) === before && value.slice(end, end + after.length) === after) {
            this.replace(start - before.length, end + after.length, selected);
            input.setSelectionRange(start - before.length, end - before.length);
            return;
        }

        this.replace(start, end, before + selected + after);
        input.setSelectionRange(start + before.length, end + before.length);
    }

    prefixLines(prefix) {
        const input = this.input;
        const { selectionStart, selectionEnd, value } = input;
        const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
        const lineEnd = value.indexOf('\n', selectionEnd) === -1 ? value.length : value.indexOf('\n', selectionEnd);
        const lines = value.slice(lineStart, lineEnd).split('\n');

        const all = lines.every((line) => line.startsWith(prefix));
        const changed = lines
            .map((line, index) => {
                if (all) return line.slice(prefix.length);
                const bare = line.replace(LIST_ITEM, '$1').replace(/^#{1,6} /, '');
                return (prefix === '1. ' ? `${index + 1}. ` : prefix) + bare;
            })
            .join('\n');

        this.replace(lineStart, lineEnd, changed);
        // A range stays selected (apply more tools); a plain caret goes to the end, ready to type.
        if (selectionStart !== selectionEnd) input.setSelectionRange(lineStart, lineStart + changed.length);
    }

    /** Replace a range as one undoable edit (and fire `input` for wire:model). */
    replace(start, end, text) {
        const input = this.input;
        input.focus();
        input.setSelectionRange(start, end);
        // Deleting via insertText('') isn't supported everywhere: fall back to setRangeText.
        if (!text || !document.execCommand?.('insertText', false, text)) {
            input.setRangeText(text, start, end, 'end');
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        // Browsers place the caret differently around line breaks – put it after the edit.
        const caret = start + text.length;
        input.setSelectionRange(caret, caret);
    }

    onKeydown(event) {
        const mod = event.ctrlKey || event.metaKey;
        if (mod && !event.altKey && !event.shiftKey) {
            const command = { b: 'bold', i: 'italic', k: 'link' }[event.key.toLowerCase()];
            if (command) {
                event.preventDefault();
                this.run(command);
                return;
            }
        }

        if (event.key === 'Enter' && !event.shiftKey && !mod && !event.isComposing) this.continueList(event);
    }

    /** Enter in a list item starts the next one; Enter in an empty item ends the list. */
    continueList(event) {
        const input = this.input;
        const { selectionStart: start, selectionEnd: end, value } = input;
        if (start !== end) return;

        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const line = value.slice(lineStart, start);
        const match = line.match(LIST_ITEM);
        if (!match) return;

        event.preventDefault();

        if (line.trim() === match[0].trim()) {
            this.replace(lineStart, start, match[1]);
            return;
        }

        let next = match[2];
        if (match[3]) next = `${Number(match[3]) + 1}${match[4]} `;
        else if (/\[[ xX]\]/.test(next)) next = next.replace(/\[[xX]\]/, '[ ]');
        this.replace(start, end, `\n${match[1]}${next}`);
    }

    onToolbarKeydown(event) {
        const buttons = this.buttons.filter((button) => !button.disabled);
        const index = buttons.indexOf(document.activeElement);
        if (index === -1) return;

        const target = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: buttons.length - 1 }[event.key];
        if (target === undefined) return;

        event.preventDefault();
        const next = buttons[(target + buttons.length) % buttons.length];
        this.buttons.forEach((button) => button.setAttribute('tabindex', button === next ? '0' : '-1'));
        next.focus();
    }
}
